/**
 * EingangScreen — T5 Eingang/Kommunikation mail-split screen.
 * Supersedes the M1 InboxSplitView skeleton at /inbox.
 * V10-J: Eingang list via useEingangMails (live GET /tasks, mock fallback) +
 * split-view detail overlay via useEingangDetail (live GET /tasks/:id).
 * V10-J2: Ausgang/Gesendet tabs via useAusgangMails (live GET /communications,
 * mock fallback) + the decision actions fire real task mutations in live mode
 * (optimistic list update + rollback + error toast); mock mode keeps the local
 * overrides overlay. Outbound send/retry/draft stay local-visual (no send
 * endpoint yet — carry-forward).
 * Migration 0102: Ablehnen/Archivieren ALSO persists server-side via useCommDismiss
 * (POST /communications/:commId/dismiss) — fixes the dismissal reappearing on a
 * reload/re-login (it used to be tracked only in the `dismissedIds` React state Set).
 *
 * [Source: docs/stories/t5-eingang-kommunikation-mailsplit.md]
 * [Source: docs/stories/v10-j-web-wiring-core.md]
 * [Source: docs/stories/v10-j2-web-wiring-rest.md AC6]
 * [Source: docs/design/2026-06-25_sebastian-ux-inventar.md Flow 1]
 * [Source: v0 components/brokly/kommunikation-view.tsx]
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Inbox,
  Send,
  CheckCheck,
  MailPlus,
  MessageSquarePlus,
  Settings2,
  ShieldCheck,
  RefreshCw,
} from 'lucide-react';
import { useNavigate, Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { SegmentedControl } from '../../components/ui/SegmentedControl';
import { Button } from '../../components/ui/Button';
import { MessageList } from './components/MessageList';
import { MailReadingPane } from './components/MailReadingPane';
import { ComposeDialog } from './components/ComposeDialog';
import { VnMessageComposeDialog } from './components/VnMessageComposeDialog';
import type { VnTarget, VnMessagePayload } from './components/VnMessageComposeDialog';
import { OutboundTable } from './components/OutboundTable';
import { OutboundDetailDialog } from './components/OutboundDetailDialog';
import { useSendVnMessage } from './hooks/useVnMessage';
import { useSendReply } from './hooks/useSendReply';
import { useCreateTask } from '../aufgaben/hooks/useCreateTask';
import { EINGANG_VIEWS, KUNDEN_OPTIONS } from './mock';
import { apiClient } from '../../lib/api-client';
import type { CreateTaskInput } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import {
  useMailboxConnections,
  useSyncMailboxNow,
  syncResultToToast,
  syncErrorToToast,
} from '../mailbox/hooks/useMailboxConnections';
import { KommunikationZeroState } from '../mailbox/components/KommunikationZeroState';
import { ProviderChoiceModal } from '../mailbox/components/ProviderChoiceModal';
import {
  useEingangMails,
  useEingangCommDetail,
  useEingangCommContent,
  useOutboundContent,
  useAusgangMails,
  useEingangDecision,
  useCommDismiss,
  useCommPrivateMark,
  useCommThreadPrivateMark,
} from './hooks/useEingangMails';
import { useRequestsForComm, useIssueAccessRequest, useDeciderRequests } from './hooks/useFreigabe';
import { FreigabenView } from './components/FreigabenView';
import { useCommAssist } from './hooks/useCommAssist';
import { applyContentToMail, applySplitViewToMail } from './api/eingang-api';
import { isLiveData } from '../../lib/data-mode';
import { EntityLink } from '../../components/ui/EntityLink';
import { invoiceTaskLink } from '../invoices/task-link';
import type { TaskProcess } from '@brokly/shared';
import type { EingangMail, AusgangMail, MailAction, KundeOption } from './types';
import type { SavedViewKey } from './mock';
import { kommunikationRoute } from '../../routeTree.gen';
import { messageForErrorReason } from '../mailbox/connect-return';

/** The Postfach segmented-control values, also the accepted `?tab=` deep-link targets. */
const TAB_KEYS = ['eingang', 'ausgang', 'gesendet', 'freigaben'] as const;

export function EingangScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const live = isLiveData();

  /* ── Delegated mailbox (MC6) — feature-detected off GET /mailbox/connections ──
   * available + 0 connections → the ⭐ zero-state replaces the Eingang split.
   * available + ≥1 connection → send-capable path (Verfassen + "Von: <email>").
   * disabled / absent → the existing screen, verbatim (no zero-state, no new UI). */
  const { availability, hasConnection, primaryConnection } = useMailboxConnections();
  const sendCapable = availability === 'available' && hasConnection;
  const showZeroState = availability === 'available' && !hasConnection;
  const sendingMailbox = primaryConnection
    ? { email: primaryConnection.email, provider: primaryConnection.provider }
    : null;
  // P2 hardening 06 — manual "Jetzt synchronisieren" for the primary mailbox.
  const syncNow = useSyncMailboxNow();
  const [composeOpen, setComposeOpen] = useState(false);
  const [providerModalOpen, setProviderModalOpen] = useState(false);

  /* ── Broker→VN message (W5) — the "Nachricht an Kunde" compose/reply.
   * Always available (channel-resolution based, independent of the delegated
   * mailbox); a selected VN thread pre-targets the reply. */
  const [vnComposeOpen, setVnComposeOpen] = useState(false);
  const [vnReplyTarget, setVnReplyTarget] = useState<VnTarget | null>(null);
  const [vnReplySubject, setVnReplySubject] = useState('');
  const sendVnMessage = useSendVnMessage();
  // Real email reply (POST /mailbox/send) + manual task create (POST /tasks).
  const sendReply = useSendReply();
  const createTask = useCreateTask();

  /* ── Eingang state ── */
  // Server state via TanStack Query (live or mock fallback — V10-J).
  const { data: mails = [], isLoading, isError } = useEingangMails();
  // Local triage-action overlay (mock-mode display; live mode mutates the cache — V10-J2).
  const [overrides, setOverrides] = useState<Record<string, Partial<EingangMail>>>({});
  const data = useMemo(
    () => mails.map((m) => (overrides[m.id] ? { ...m, ...overrides[m.id] } : m)),
    [mails, overrides],
  );
  const [tab, setTab] = useState('eingang');
  /* ── Deep-link into a tab (issue 33) ──
   * The Freigabe-Anfrage notification links to /kommunikation?tab=freigaben. The tab is
   * local state (it reconfigures the view in place), so the search param only SEEDS it:
   * an effect, not a mount-only read, because the bell can fire this while the screen is
   * already open, then the component never remounts and a mount-only read would miss it.
   * An unknown value is ignored (never blanks the screen). */
  const { tab: tabParam, mail: mailParam } = kommunikationRoute.useSearch();
  useEffect(() => {
    if (tabParam && (TAB_KEYS as readonly string[]).includes(tabParam)) setTab(tabParam);
  }, [tabParam]);
  /* Pending Freigabe-Anfragen for the tab badge (issue 33). Lifted out of FreigabenView so
     the count shows without opening the tab; React Query dedupes the shared key, so the tab
     content does not fetch twice. */
  const { data: pendingFreigaben = [] } = useDeciderRequests('pending');
  const [viewKey, setViewKey] = useState<SavedViewKey>('alle');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [taskedIds, setTaskedIds] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<string | null>(null);
  const [errorFeedback, setErrorFeedback] = useState<string | null>(null);
  // Locally-dismissed communications (Ablehnen / Archivieren) — the OPTIMISTIC overlay for
  // instant feedback. Migration 0102 made the dismissal PERSIST server-side
  // (communication.dismissed_at; GET /communications filters it out of every bucket), so a
  // reload/re-login no longer brings the message back — this Set just avoids a flash of the
  // row before the live query (which the commDismiss mutation also patches optimistically)
  // settles, and covers the mock-mode fallback (no backend, no server flag to rely on).
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  function setOverride(id: string, patch: Partial<EingangMail>) {
    setOverrides((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  function dismiss(id: string) {
    setDismissedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }

  /* ── Decision mutation (V10-J2 — live mode only) ── */
  const decision = useEingangDecision((msg) => {
    setErrorFeedback(msg);
    setTimeout(() => setErrorFeedback(null), 4000);
  });
  /* ── Dismiss mutation (migration 0102 — live mode only): persists Ablehnen/Archivieren
     server-side, fired ALONGSIDE decision.mutate (not instead of it — see applyDecision). ── */
  const commDismiss = useCommDismiss((msg) => {
    setErrorFeedback(msg);
    setTimeout(() => setErrorFeedback(null), 4000);
  });
  /* ── Privat-Markierung mutation (Berechtigungskonzept V3.0 §2.2 — live mode only). ── */
  const commPrivateMark = useCommPrivateMark((msg) => {
    setErrorFeedback(msg);
    setTimeout(() => setErrorFeedback(null), 4000);
  });
  /* ── P4: mark the WHOLE thread private (live mode only). ── */
  const commThreadPrivateMark = useCommThreadPrivateMark((msg) => {
    setErrorFeedback(msg);
    setTimeout(() => setErrorFeedback(null), 4000);
  });

  /* ── Ausgehend state (V10-J2: query data + local status overlay) ── */
  const { data: ausgangRows = [] } = useAusgangMails('ausgang_entwuerfe');
  const { data: gesendetRows = [] } = useAusgangMails('gesendet');
  const [outOverrides, setOutOverrides] = useState<Record<string, Partial<AusgangMail>>>({});
  const [detail, setDetail] = useState<AusgangMail | null>(null);
  /* N2: the sent message's HTML body (real formatting + inlined logos) for the
     open outbound detail. Fetched via the shared /content endpoint. */
  const { data: detailContent } = useOutboundContent(detail?.id ?? null);

  const outbound = useMemo(
    () =>
      [...ausgangRows, ...gesendetRows].map((m) =>
        outOverrides[m.id] ? { ...m, ...outOverrides[m.id] } : m,
      ),
    [ausgangRows, gesendetRows, outOverrides],
  );

  function setOutOverride(id: string, patch: Partial<AusgangMail>) {
    setOutOverrides((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  }

  /* ── Counts ── */
  const neuCount = useMemo(() => data.filter((m) => m.triage === 'neu').length, [data]);

  /* ── Filtering / sorting ── */
  const activeView = EINGANG_VIEWS.find((v) => v.key === viewKey);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return data
      // Rejected/archived messages leave the decision queue immediately (Ablehnen dismiss).
      .filter((m) => !dismissedIds.has(m.id))
      .filter((m) => (activeView?.triage ? m.triage === activeView.triage : true))
      .filter((m) =>
        q
          ? `${m.absender} ${m.absenderRolle} ${m.betreff} ${m.kunde}`.toLowerCase().includes(q)
          : true,
      );
    // oldest-first within group is handled by MessageList buckets
  }, [data, activeView, search, dismissedIds]);

  /* Keep selected id inside filtered list */
  useEffect(() => {
    if (filtered.length === 0) {
      setSelectedId(null);
    } else if (!filtered.some((m) => m.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  /* ── Deep-link one mail: /kommunikation?mail=<commId> ──
   * The Posteingang's Betreff links here — "show me this mail as I normally read it",
   * as opposed to its row, which opens the Bearbeitungsmaske. `EingangMail.id` IS the
   * communication id (mapCommToEingangMail), so the Posteingang row id addresses a mail
   * directly, no translation.
   *
   * It waits for the mail to actually BE in `filtered` (the list arrives async, and the
   * effect above would otherwise immediately bounce the selection to filtered[0]), and
   * a ref makes it fire ONCE per id: after that the broker's own clicks own the
   * selection, and re-selecting a row must not snap back to the linked mail. A mail that
   * never shows up (dismissed, or outside the active saved view) simply leaves the
   * default selection alone — the list is still right, just not scrolled where asked. */
  const honouredMailParam = useRef<string | null>(null);
  useEffect(() => {
    if (!mailParam || honouredMailParam.current === mailParam) return;
    if (!filtered.some((m) => m.id === mailParam)) return;
    honouredMailParam.current = mailParam;
    setSelectedId(mailParam);
  }, [mailParam, filtered]);

  const selectedIndex = filtered.findIndex((m) => m.id === selectedId);
  const selectedBase = selectedIndex >= 0 ? filtered[selectedIndex] : null;

  /* Split-view detail overlay (PART A3: comm-keyed; resolves the comm's task for the
     body). Only fetched for a FULL-tier row with a task — reduced/no-task rows render
     from row data (overlay / no body). splitView may be null (no task). */
  const { data: splitView } = useEingangCommDetail(selectedBase?.id ?? null, {
    tier: selectedBase?.tier,
    hasTask: selectedBase?.hasTask,
  });
  /* Fix C: fetch the body for the SELECTED comm regardless of a linked task, so
     no-task and HTML-only mail both render an actual body. The content endpoint is
     the authoritative body source → it overlays last, over the split-view fields. */
  const { data: commContent } = useEingangCommContent(selectedBase?.id ?? null, {
    tier: selectedBase?.tier,
  });
  const selected = useMemo(() => {
    let mail =
      selectedBase && splitView ? applySplitViewToMail(selectedBase, splitView) : selectedBase;
    if (mail && commContent && commContent.your_effective_tier === 'full') {
      mail = applyContentToMail(mail, commContent);
    }
    return mail;
  }, [selectedBase, splitView, commContent]);

  /* PART B / W1 — reduced-tier Freigabe state. For a reduced-tier viewer the API
     returns ONLY their own requests on the comm, so any pending row = an open
     request by the caller (→ pending badge; hide the request button). */
  const selectedReduced = selectedBase?.tier === 'title' || selectedBase?.tier === 'metadata';
  const { data: commRequests } = useRequestsForComm(
    selectedBase?.id ?? null,
    Boolean(selectedReduced),
  );
  const openRequest = commRequests?.find((r) => r.status === 'pending') ?? null;
  const issueRequest = useIssueAccessRequest((msg) => {
    setErrorFeedback(msg);
    setTimeout(() => setErrorFeedback(null), 4000);
  });

  /* KI-Assistenz (UI-U9): feature-detected via the SPLIT-VIEW communication id
     (not the task id); mock mode derives from the mock KI fields. */
  const assist = useCommAssist(splitView?.communication?.id ?? null, selected);
  /* The reply's communication id — threads the sent reply (in_reply_to) + powers the
     "KI-Vorschlag generieren" refresh. Only a real (live, task-backed) comm has one. */
  const replyCommId = splitView?.communication?.id ?? null;

  /* ── Navigation helpers ── */
  function goNext() {
    if (selectedIndex >= 0 && selectedIndex < filtered.length - 1)
      setSelectedId(filtered[selectedIndex + 1].id);
  }
  function goPrev() {
    if (selectedIndex > 0) setSelectedId(filtered[selectedIndex - 1].id);
  }

  function showFeedback(msg: string) {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 2500);
  }

  /* P2 hardening 06 — run one manual sync round for the primary mailbox, then surface the
   * count (useSyncMailboxNow already invalidates the Posteingang/Eingang queries). */
  function triggerSync(connectionId: string) {
    syncNow.mutate(connectionId, {
      onSuccess: (r) => {
        const t = syncResultToToast(r);
        if (t.kind === 'success') {
          showFeedback(t.message);
        } else {
          setErrorFeedback(t.message);
          setTimeout(() => setErrorFeedback(null), 4000);
        }
      },
      onError: (e) => {
        setErrorFeedback(syncErrorToToast(e).message);
        setTimeout(() => setErrorFeedback(null), 4000);
      },
    });
  }

  /* ── Mailbox-connect OAuth return handler (ported from PostfaecherPage) ──
   * The OAuth callback redirects here (/kommunikation) with ?mailbox=connected|error.
   * Success → confirm + refresh the connection list; error → GENERIC toast (the raw
   * `reason` only branches, never hits the DOM). The query param is stripped so a
   * reload / back does not re-toast. Mount-only (qc is stable). */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('mailbox');
    if (result !== 'connected' && result !== 'error') return;

    if (result === 'connected') {
      showFeedback('Postfach erfolgreich verbunden.');
      void qc.invalidateQueries({ queryKey: qk.mailboxConnections() });
    } else {
      setErrorFeedback(messageForErrorReason(params.get('reason')));
      setTimeout(() => setErrorFeedback(null), 4000);
    }
    // Strip the query param (keep the current path) so a reload does not re-toast.
    window.history.replaceState({}, '', window.location.pathname);
    // Mount-only: read the initial callback params exactly once (qc is stable).
  }, []);

  /* ── Eingang actions ──
   * live mode (V10-J2): real task mutations (complete/cancel) with an optimistic
   * cache update + rollback on error (useEingangDecision).
   * mock mode: local display overlay (pre-V10-J2 behaviour, kept as fallback). */
  function markBeantwortet(id: string) {
    setOverride(id, { triage: 'beantwortet' });
  }

  function applyDecision(action: MailAction) {
    if (!selected) return;
    if (action !== 'bestaetigen' && action !== 'archivieren' && action !== 'ablehnen') return;

    // PART A3: task actions need the TASK id (the row id is a comm id). Resolve it from
    // the split-view; a comm with no task (internal / newsletter) has no task to act on.
    const taskId = splitView?.task?.id ?? null;
    const taskStatus = splitView?.task?.status ?? null;
    const isTerminal = taskStatus === 'done' || taskStatus === 'cancelled';

    if (action === 'ablehnen' || action === 'archivieren') {
      // Always dismiss the message from the decision queue — this is the behaviour the
      // broker expects ("make the spam go away") and it works regardless of task state:
      // no-task mail has nothing to cancel, and firing cancel on an already-terminal task
      // would be a visible no-op. So we hide the comm here (optimistic overlay) and, in
      // live mode, ALSO persist it server-side (migration 0102 — commDismiss) so it stays
      // gone across reload/re-login; the task is only touched when there is a LIVE
      // (non-terminal) one to resolve.
      dismiss(selected.id);
      if (live) {
        if (taskId && !isTerminal) {
          // ablehnen → cancel the task · archivieren → complete it (useEingangDecision).
          decision.mutate({ taskId, commId: selected.id, action });
        }
        // Persists regardless of task state — a no-task (internal/newsletter) comm has
        // nothing to cancel/complete but still needs the server-side dismiss flag.
        commDismiss.mutate(selected.id);
      } else {
        markBeantwortet(selected.id);
      }
    } else {
      // bestaetigen — mark the linked task done (unchanged optimistic behaviour; keeps the
      // comm in "Alle" as 'beantwortet', only leaving the triage-filtered saved views).
      if (live) {
        if (taskId && !isTerminal) decision.mutate({ taskId, commId: selected.id, action });
      } else {
        markBeantwortet(selected.id);
      }
    }

    const labels: Partial<Record<MailAction, string>> = {
      // Honest: Bestätigen just marks the linked task done — it does NOT send anything.
      bestaetigen: 'Bestätigt, Aufgabe erledigt',
      archivieren: 'Nachricht archiviert',
      ablehnen: 'Nachricht abgelehnt',
    };
    showFeedback(labels[action] ?? 'Erledigt');
    // A dismissed row disappears from `filtered`; the selection effect reselects the next
    // valid row automatically, so only advance explicitly for the non-dismissing confirm.
    if (action === 'bestaetigen') goNext();
  }

  /**
   * Create a REAL task from the email (POST /tasks — manual create), linking the
   * matched customer as the subject and the source communication in meta. Mock mode
   * resolves without network (useCreateTask). On success the task shows in Aufgaben
   * (the mutation invalidates the shared ['tasks','list'] key family).
   */
  function handleCreateTask(mail: EingangMail) {
    const st = mail.subjectType;
    const validSubjectType =
      st === 'person' || st === 'firma' || st === 'invoice' || st === 'commission_line'
        ? st
        : undefined;
    const body: CreateTaskInput = {
      title: `Nachverfolgen: ${mail.betreff}`,
      ...(validSubjectType && mail.kundeId
        ? { subject_type: validSubjectType, subject_id: mail.kundeId }
        : {}),
      meta: { source: 'kommunikation', comm_id: mail.id },
    };
    createTask.mutate(body, {
      onSuccess: () => {
        setTaskedIds((prev) => new Set(prev).add(mail.id));
        showFeedback('Aufgabe erstellt');
      },
      onError: () => {
        setErrorFeedback('Aufgabe konnte nicht erstellt werden.');
        setTimeout(() => setErrorFeedback(null), 4000);
      },
    });
  }

  function matchKunde(kunde: KundeOption) {
    if (!selected) return;
    setOverride(selected.id, { kundeMatched: true, kunde: kunde.name, kundeId: kunde.id });
    showFeedback(`Kunde zugeordnet: ${kunde.name}`);
  }

  function openKunde(kundeId: string) {
    void navigate({ to: '/customers/$id', params: { id: kundeId } });
  }

  /* ── Broker→VN message (W5) helpers ──
   * VN search feeds the compose picker: live → GET /customers?q; mock → the
   * KUNDEN_OPTIONS mock. Send hits POST /communications/vn-messages; the created
   * outbound communication surfaces in Ausgang/Gesendet (the hook invalidates). */
  const searchVnCustomers = useCallback(
    async (q: string): Promise<VnTarget[]> => {
      if (!live) {
        const needle = q.trim().toLowerCase();
        return KUNDEN_OPTIONS.filter((k) => k.name.toLowerCase().includes(needle)).map((k) => ({
          subjectType: 'person' as const,
          subjectId: k.id,
          name: k.name,
        }));
      }
      const res = await apiClient.customers.list({ q: q.trim() || undefined, limit: 8 });
      return res.items
        .filter((i) => i.subject_id)
        .map((i) => ({
          subjectType: i.subject_type,
          subjectId: i.subject_id,
          name: i.name ?? '(ohne Namen)',
        }));
    },
    [live],
  );

  const sendVnMessagePayload = useCallback(
    (payload: VnMessagePayload) => sendVnMessage.mutateAsync(payload),
    [sendVnMessage],
  );

  function openVnCompose() {
    // Pre-target the reply when a VN customer is selected in the Eingang.
    if (
      selected &&
      selected.kundeId &&
      (selected.subjectType === 'person' || selected.subjectType === 'firma')
    ) {
      setVnReplyTarget({
        subjectType: selected.subjectType,
        subjectId: selected.kundeId,
        name: selected.kunde || '(Kunde)',
      });
      setVnReplySubject(selected.betreff ? `Re: ${selected.betreff}` : '');
    } else {
      setVnReplyTarget(null);
      setVnReplySubject('');
    }
    setVnComposeOpen(true);
  }

  /* ── Ausgehend actions (local-visual overlay — no send endpoint yet, V10-J2) ── */
  const ausgang = outbound.filter((m) => m.status !== 'gesendet');
  const gesendet = outbound.filter((m) => m.status === 'gesendet');

  function retry(id: string) {
    setOutOverride(id, { status: 'versandbereit' });
    showFeedback('Erneut zum Versand vorgemerkt');
  }

  function sendOutbound(id: string) {
    setOutOverride(id, { status: 'gesendet' });
    showFeedback('Nachricht gesendet');
    setDetail(null);
  }

  function saveDraft(id: string) {
    setOutOverride(id, { status: 'entwurf' });
    showFeedback('Als Entwurf gespeichert');
    setDetail(null);
  }

  /* ── Keyboard J/K/E (skip when focus in INPUT/TEXTAREA) ── */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'j' || e.key === 'J') {
        e.preventDefault();
        goNext();
      } else if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        goPrev();
      } else if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        applyDecision('bestaetigen');
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedIndex, filtered]); // goNext/goPrev/applyDecision are stable within this dep scope

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      {/* Feedback banner (aria-live) */}
      {feedback && (
        <div
          role="status"
          aria-live="polite"
          className="fixed bottom-4 right-4 z-50 flex items-center gap-2 rounded-lg border border-sem-ok/40 bg-sem-ok-bg px-4 py-2.5 text-sm text-sem-ok shadow-md"
        >
          {feedback}
        </div>
      )}

      {/* Error toast (V10-J2 — mutation failure after optimistic rollback) */}
      {errorFeedback && (
        <div
          role="alert"
          className="fixed bottom-16 right-4 z-50 flex items-center gap-2 rounded-lg border border-sem-alert/40 bg-sem-alert-bg px-4 py-2.5 text-sm text-sem-alert shadow-md"
        >
          {errorFeedback}
        </div>
      )}

      <div className="flex flex-1 flex-col gap-0 overflow-hidden">
        {/* Scope switch + MC6 mailbox actions (Verfassen / Postfächer). The actions
            render only when the delegated feature is present (availability !== 'absent')
            so a non-delegated tenant's Kommunikation header is unchanged. */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SegmentedControl
            ariaLabel="Postfach"
            value={tab}
            onValueChange={setTab}
            options={[
              {
                value: 'eingang',
                label: 'Eingang',
                icon: <Inbox className="size-4" />,
                count: neuCount > 0 ? neuCount : undefined,
              },
              {
                value: 'ausgang',
                label: 'Ausgang',
                icon: <Send className="size-4" />,
                count: ausgang.length > 0 ? ausgang.length : undefined,
              },
              {
                value: 'gesendet',
                label: 'Gesendet',
                icon: <CheckCheck className="size-4" />,
                count: gesendet.length > 0 ? gesendet.length : undefined,
              },
              {
                value: 'freigaben',
                label: 'Freigaben',
                icon: <ShieldCheck className="size-4" />,
                count: pendingFreigaben.length > 0 ? pendingFreigaben.length : undefined,
              },
            ]}
          />
          <div className="flex items-center gap-2">
            {/* Broker→VN message (W5) — channel-resolution based, always available.
                A selected VN thread pre-targets the reply. */}
            <Button size="sm" onClick={openVnCompose}>
              <MessageSquarePlus className="size-4" /> Nachricht an Kunde
            </Button>
            {availability !== 'absent' && (
              <>
                {/* P2 hardening 06 — "Jetzt synchronisieren" the primary mailbox (one bounded round). */}
                {hasConnection && primaryConnection && (
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={syncNow.isPending}
                    onClick={() => triggerSync(primaryConnection.id)}
                    title="Jetzt synchronisieren (primäres Postfach)"
                  >
                    <RefreshCw className={`size-4${syncNow.isPending ? ' animate-spin' : ''}`} />
                    Jetzt synchronisieren
                  </Button>
                )}
                {sendCapable && (
                  <Button size="sm" variant="outline" onClick={() => setComposeOpen(true)}>
                    <MailPlus className="size-4" /> Verfassen
                  </Button>
                )}
                <Link
                  to="/admin/$section"
                  params={{ section: 'postfaecher' }}
                  className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <Settings2 className="size-3.5" /> Postfächer verwalten
                </Link>
              </>
            )}
          </div>
        </div>

        {tab === 'eingang' && (
          <div className="mt-4 flex min-h-0 flex-1 overflow-hidden rounded-xl border bg-card">
            {showZeroState ? (
              <KommunikationZeroState availability={availability} />
            ) : isLoading ? (
              <div className="flex h-full w-full items-center justify-center p-8 text-sm text-muted-foreground">
                Lade Nachrichten …
              </div>
            ) : isError ? (
              <div
                role="alert"
                className="flex h-full w-full items-center justify-center p-8 text-sm text-muted-foreground"
              >
                Nachrichten konnten nicht geladen werden.
              </div>
            ) : (
              <>
                {/* Responsive split: list above pane on narrow, side-by-side on lg */}
                <div className="grid h-full w-full min-h-0 grid-rows-[auto_1fr] overflow-hidden lg:grid-cols-[20rem_1fr] lg:grid-rows-1">
                  {/* LEFT: message list */}
                  <div className="min-h-[16rem] overflow-hidden border-b border-border lg:border-b-0 lg:border-r">
                    <MessageList
                      messages={filtered}
                      selectedId={selectedId}
                      onSelect={setSelectedId}
                      viewKey={viewKey}
                      onViewChange={setViewKey}
                      allMessages={data}
                      search={search}
                      onSearchChange={setSearch}
                      filteredCount={filtered.length}
                    />
                  </div>
                  {/* RIGHT: reading pane */}
                  <div className="min-h-0 overflow-hidden">
                    {/* Story UI-U4: invoice tasks deep-link into the Rechnungskontrolle peek. */}
                    {selected &&
                      (() => {
                        const link = invoiceTaskLink({
                          process: selected.kategorie as TaskProcess,
                          subject_type: selected.subjectType ?? null,
                          subject_id: selected.kundeId || null,
                        });
                        return link ? (
                          <div className="border-b border-border px-4 py-2">
                            <EntityLink onActivate={() => void navigate(link)}>
                              Rechnung öffnen
                            </EntityLink>
                          </div>
                        ) : null;
                      })()}
                    <MailReadingPane
                      mail={selected}
                      index={selectedIndex < 0 ? 0 : selectedIndex}
                      total={filtered.length}
                      onPrev={goPrev}
                      onNext={goNext}
                      onDecision={applyDecision}
                      onCreateTask={handleCreateTask}
                      onOpenKunde={openKunde}
                      onMatchKunde={matchKunde}
                      hasLinkedTask={selected ? taskedIds.has(selected.id) : false}
                      assist={assist}
                      commId={replyCommId}
                      onReplySend={
                        live && sendCapable && replyCommId
                          ? async (draft) => {
                              await sendReply.mutateAsync({
                                to: [draft.to],
                                subject: draft.betreff,
                                body_text: draft.text,
                                // B3 rich: the HTML body carries the sanitised rich signature; the
                                // server sanitiser is the authority over whatever markup arrives.
                                ...(draft.bodyHtml ? { body_html: draft.bodyHtml } : {}),
                                in_reply_to_communication_id: replyCommId,
                              });
                              setTab('gesendet');
                              showFeedback('Antwort gesendet');
                            }
                          : undefined
                      }
                      sendingMailbox={sendingMailbox}
                      onConnectRequest={
                        availability === 'available' && !hasConnection
                          ? () => setProviderModalOpen(true)
                          : undefined
                      }
                      accessRequestPending={Boolean(openRequest)}
                      accessRequestedAt={openRequest?.created_at ?? null}
                      onRequestAccess={
                        selectedReduced && selected
                          ? (reason: string) => {
                              issueRequest.mutate(
                                { commId: selected.id, reason },
                                { onSuccess: () => showFeedback('Freigabe angefragt') },
                              );
                            }
                          : undefined
                      }
                      requestInFlight={issueRequest.isPending}
                      onTogglePrivate={
                        live && selected
                          ? () => {
                              commPrivateMark.mutate(
                                { commId: selected.id, makePrivate: !selected.isPrivate },
                                {
                                  onSuccess: () =>
                                    showFeedback(
                                      selected.isPrivate
                                        ? 'Nicht mehr privat'
                                        : 'Als privat markiert',
                                    ),
                                },
                              );
                            }
                          : undefined
                      }
                      privateActionInFlight={commPrivateMark.isPending}
                      onToggleThreadPrivate={
                        live && selected
                          ? () => {
                              commThreadPrivateMark.mutate(
                                { commId: selected.id, makePrivate: !selected.isPrivate },
                                {
                                  onSuccess: () =>
                                    showFeedback(
                                      selected.isPrivate
                                        ? 'Thread nicht mehr privat'
                                        : 'Ganzer Thread als privat markiert',
                                    ),
                                },
                              );
                            }
                          : undefined
                      }
                      threadPrivateActionInFlight={commThreadPrivateMark.isPending}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {tab === 'ausgang' && (
          <div className="mt-4">
            <OutboundTable
              rows={ausgang}
              onOpenDetail={setDetail}
              onOpenKunde={openKunde}
              onRetry={retry}
              emptyText="Keine ausgehenden Nachrichten."
            />
          </div>
        )}

        {tab === 'gesendet' && (
          <div className="mt-4">
            <OutboundTable
              rows={gesendet}
              onOpenDetail={setDetail}
              onOpenKunde={openKunde}
              emptyText="Noch nichts gesendet."
            />
          </div>
        )}

        {/* W2 — Freigaben (decider inbox): access requests the caller may decide. */}
        {tab === 'freigaben' && (
          <div className="mt-4 min-h-0 flex-1 overflow-y-auto">
            <FreigabenView onFeedback={showFeedback} onError={setErrorFeedback} />
          </div>
        )}
      </div>

      <OutboundDetailDialog
        mail={detail}
        open={detail !== null}
        onOpenChange={(o) => {
          if (!o) setDetail(null);
        }}
        onSend={sendOutbound}
        onSave={saveDraft}
        bodyHtml={detailContent?.body_html ?? null}
      />

      {/* MC6 — "Verfassen" new-message compose (mail-less). Draft-only until the
          send path lands (mc5's «Senden»); the sending mailbox is surfaced. */}
      <ComposeDialog
        mail={null}
        open={composeOpen}
        mode="compose"
        initialDraft={{ to: '', betreff: '', text: '' }}
        onOpenChange={setComposeOpen}
        onSaveDraft={() => {
          setComposeOpen(false);
          showFeedback('Als Entwurf gespeichert');
        }}
        sendingMailbox={sendingMailbox}
        onConnectRequest={() => setProviderModalOpen(true)}
      />

      {/* MC6 — shared provider-choice modal for the compose / reading-pane connect hint. */}
      <ProviderChoiceModal
        open={providerModalOpen}
        onClose={() => setProviderModalOpen(false)}
        availability={availability}
      />

      {/* W5 — broker→VN "Nachricht an Kunde" compose/reply. On send the outbound
          communication surfaces in Ausgang/Gesendet (hook invalidates); we also
          jump to Gesendet + confirm so the broker sees their sent message. */}
      <VnMessageComposeDialog
        open={vnComposeOpen}
        onOpenChange={setVnComposeOpen}
        initialTarget={vnReplyTarget}
        initialSubject={vnReplySubject}
        searchCustomers={searchVnCustomers}
        onSend={sendVnMessagePayload}
        onSent={(res) => {
          setTab('gesendet');
          showFeedback(
            res.delivery === 'portal'
              ? 'Nachricht im Kundenportal zugestellt'
              : 'Nachricht als E-Mail gesendet',
          );
        }}
      />
    </div>
  );
}
